import React, { useCallback, useEffect, useState } from 'react';
import { CANONICAL_ORIGIN, findRoute, normalizePath } from './routes.js';

const NAV_EVENT = 'epochtimer:navigate';

/** Push a new URL and tell every subscriber to re-render. */
export function navigate(path: string, { replace = false } = {}) {
  const target = normalizePath(path);
  if (normalizePath(window.location.pathname) === target) return;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', target);
  window.dispatchEvent(new Event(NAV_EVENT));
}

/** Current pathname, kept in sync with back/forward and in-app navigation. */
export function usePathname(): string {
  const [pathname, setPathname] = useState(() =>
    normalizePath(window.location.pathname)
  );

  useEffect(() => {
    const sync = () => setPathname(normalizePath(window.location.pathname));
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, []);

  return pathname;
}

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
}

/**
 * A real <a href> so crawlers can follow it, upgraded to a History API push on
 * plain left-clicks. Modifier-clicks and middle-clicks fall through to the
 * browser so "open in new tab" keeps working.
 */
export const Link: React.FC<LinkProps> = ({ href, onClick, children, ...rest }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate(href);
    },
    [href, onClick]
  );

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
};

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Keep <title>, description, canonical and OG tags in step with the route. */
export function useDocumentHead(pathname: string) {
  useEffect(() => {
    const route = findRoute(pathname);
    const url = `${CANONICAL_ORIGIN}${pathname === '/' ? '/' : pathname}`;

    const title = route ? route.title : 'Page Not Found | EpochTimer';
    const description = route
      ? route.description
      : 'That page does not exist on EpochTimer.';

    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    // Unknown URLs must not claim a canonical, and must not be indexed.
    setMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      route
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, follow'
    );

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (route) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = url;
    } else if (canonical) {
      canonical.remove();
    }
  }, [pathname]);
}
